import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FileCheck2, Upload, Eye, Trash2, RefreshCw, Lock, Loader2, ShieldCheck } from 'lucide-react';

/**
 * IssuedPermitCard — manage the sealed (legally-signed) issued permit PDF.
 * The document is read-only: it can be viewed/downloaded but never edited.
 * Only admins can replace/delete it; every change is logged server-side.
 */
export function IssuedPermitCard({ permit, counties = [], isAdmin = false, onUpdated }) {
  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  const issued = permit?.issued_permit || null;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [permitNumber, setPermitNumber] = useState(permit?.county_permit_number || '');
  const [county, setCounty] = useState(permit?.county || '');
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(false);

  const openUpload = () => {
    setFile(null);
    setPermitNumber(permit?.county_permit_number || '');
    setCounty(permit?.county || '');
    setDialogOpen(true);
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Select the issued permit PDF'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('bw_token');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('permit_number', permitNumber || '');
      fd.append('county', county || '');
      const res = await fetch(`${apiUrl}/api/permits/${permit.id}/issued-pdf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const updated = await res.json();
      toast.success(issued ? 'Issued permit replaced' : 'Issued permit uploaded');
      setDialogOpen(false);
      onUpdated && onUpdated(updated);
    } catch (e) {
      toast.error(e.message || 'Error uploading the issued permit');
    } finally {
      setSaving(false);
    }
  };

  const handleView = async () => {
    setViewing(true);
    try {
      const token = localStorage.getItem('bw_token');
      const res = await fetch(`${apiUrl}/api/permits/${permit.id}/issued-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Could not open the file');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.message || 'Error opening the issued permit');
    } finally {
      setViewing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete the issued permit? This is a sealed document and the action will be logged.')) return;
    try {
      const token = localStorage.getItem('bw_token');
      const res = await fetch(`${apiUrl}/api/permits/${permit.id}/issued-pdf`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Delete failed');
      }
      const updated = await res.json();
      toast.success('Issued permit deleted');
      onUpdated && onUpdated(updated);
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  const fmtSize = (b) => {
    if (!b) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4 py-2" data-testid="issued-permit-section">
      <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-emerald-700">
          Upload the PDF of the permit issued by the county (SWFWMD/SFWMD). This is a sealed legal document:
          it can be viewed and downloaded, but <span className="font-semibold">cannot be edited</span>.
        </p>
      </div>

      {issued ? (
        <div className="border border-gray-200 rounded-lg p-4 space-y-3" data-testid="issued-permit-file">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-[#003366]/10 flex items-center justify-center flex-shrink-0">
                <FileCheck2 className="w-5 h-5 text-[#003366]" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                  {issued.filename || 'permit.pdf'}
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                    <Lock className="w-2.5 h-2.5" /> Read-only
                  </span>
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {fmtSize(issued.file_size)}
                  {issued.uploaded_at ? ` · ${issued.uploaded_at.slice(0, 10)}` : ''}
                  {issued.uploaded_by_name ? ` · ${issued.uploaded_by_name}` : ''}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-gray-400">Permit Number</span><p className="font-mono font-semibold text-gray-800">{permit.county_permit_number || '—'}</p></div>
            <div><span className="text-gray-400">County</span><p className="text-gray-800">{permit.county || '—'}</p></div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={handleView} disabled={viewing} data-testid="issued-permit-view-btn">
              {viewing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />} View / Download
            </Button>
            {isAdmin && (
              <>
                <Button size="sm" variant="outline" onClick={openUpload} data-testid="issued-permit-replace-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Replace
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={handleDelete} data-testid="issued-permit-delete-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                </Button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center" data-testid="issued-permit-empty">
          <FileCheck2 className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500 mb-3">No issued permit uploaded yet</p>
          <Button onClick={openUpload} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="issued-permit-upload-btn">
            <Upload className="w-4 h-4 mr-2" /> Upload Issued Permit
          </Button>
        </div>
      )}

      {/* Upload / Replace dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) setDialogOpen(false); }}>
        <DialogContent className="sm:max-w-[460px]" data-testid="issued-permit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck2 className="w-5 h-5 text-[#003366]" />
              {issued ? 'Replace Issued Permit' : 'Upload Issued Permit'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Sealed permit PDF *</Label>
              <Input type="file" accept="application/pdf,.pdf" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="issued-permit-file-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Permit Number</Label>
              <Input value={permitNumber} onChange={e => setPermitNumber(e.target.value)} placeholder="e.g. SAR-2026-9988" className="font-mono" data-testid="issued-permit-number-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">County</Label>
              <Select value={county || '_none'} onValueChange={v => setCounty(v === '_none' ? '' : v)}>
                <SelectTrigger data-testid="issued-permit-county-select"><SelectValue placeholder="Select county" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-- Select --</SelectItem>
                  {counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={saving || !file} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="issued-permit-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />} {issued ? 'Replace' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default IssuedPermitCard;
